/**
 * Generic staggered appear (translate or scale + opacity) via Motion.
 * Sections: data-stagger-appear="true", data-appear-style="translate"|"scale", data-appear-item on children.
 * Optional: data-appear-pairs="true" + data-appear-order (number) groups items that share the same step delay
 * (e.g. competition table row N left + row N right animate together).
 * Optional: data-appear-sequence="radial" reveals centre-out (middle item(s) first); default is linear.
 *
 * Animation modes (data-appear-mode):
 * - "intersection" (default) — one-shot staggered reveal when section enters viewport
 * - "scroll" — scroll-linked progressive per-item animation (each item animates as it scrolls into view)
 *
 * Timing/easing come from the global Theme default (Settings › Animations, exposed
 * as window.__288Stagger). A section can override any of them per-section via the
 * data-appear-* attributes emitted by snippets/288-appear.liquid — see
 * getSectionConfig(). The 288-schema-appear skill is the authoring entry point.
 */
const SECTION_SELECTOR = '[data-stagger-appear="true"]';
const ITEM_SELECTOR = "[data-appear-item]";

/**
 * Named easing presets for the transform (translate/scale). The opacity fade is
 * NOT in here on purpose: per the motion-token rule, alpha always animates
 * linearly — only the movement gets a curve.
 * @type {Record<string, number[] | "linear">}
 */
const EASE_PRESETS = {
  "expo-out": [0.19, 1, 0.22, 1],
  "cubic-out": [0.215, 0.61, 0.355, 1],
  "quad-out": [0.25, 0.46, 0.45, 0.94],
  "sine-out": [0.39, 0.575, 0.565, 1],
  linear: "linear",
};
const DEFAULT_EASE = "expo-out";
/** Opacity always fades linearly. */
const EASE_OPACITY = "linear";

/**
 * Reads the stagger config exposed by the theme (Settings › Animations ›
 * Stagger), falling back to the historical defaults when a value is missing or
 * invalid. Durations/delay are authored in ms in the admin, used here in seconds.
 * @returns {{ durationS: number, opacityDurationS: number, stepDelayS: number, stepScroll: number, scrollDistance: number, easeInView: number[] | "linear", easeScroll: number[] | "linear" }}
 */
function readStaggerConfig() {
  const cfg = (typeof window !== "undefined" && window.__288Stagger) || {};
  /** ms → seconds. @param {unknown} v @param {number} fallback */
  const ms = (v, fallback) => {
    const n = typeof v === "number" ? v : parseFloat(String(v));
    return Number.isFinite(n) && n >= 0 ? n / 1000 : fallback;
  };
  /** percent → fraction. @param {unknown} v @param {number} fallback */
  const frac = (v, fallback) => {
    const n = typeof v === "number" ? v : parseFloat(String(v));
    return Number.isFinite(n) && n >= 0 ? n / 100 : fallback;
  };
  /** @param {unknown} v */
  const resolveEase = (v) =>
    EASE_PRESETS[typeof v === "string" ? v : DEFAULT_EASE] ||
    EASE_PRESETS[DEFAULT_EASE];
  return {
    durationS: ms(cfg.duration, 0.5),
    opacityDurationS: ms(cfg.opacityDuration, 0.7),
    // Intersection/eager: time between successive items.
    stepDelayS: ms(cfg.delay, 0.12),
    // Scroll: per-item offset as a fraction of the viewport.
    stepScroll: frac(cfg.stepScroll, 0.12),
    // Scroll: scroll distance over which an item's movement plays (the "duration"
    // equivalent for scroll-linked mode), as a fraction of the viewport.
    scrollDistance: frac(cfg.scrollDistance, 0.2),
    // Separate movement curves per mode (opacity always fades linearly).
    easeInView: resolveEase(cfg.ease),
    easeScroll: resolveEase(cfg.easeScroll),
  };
}

const STAGGER = readStaggerConfig();
const EASE_IN_VIEW = STAGGER.easeInView;
const EASE_SCROLL = STAGGER.easeScroll;
const DURATION_S = STAGGER.durationS;
const OPACITY_DURATION_S = STAGGER.opacityDurationS;
const STEP_DELAY_S = STAGGER.stepDelayS;
const STEP_SCROLL = STAGGER.stepScroll;
const SCROLL_DISTANCE = STAGGER.scrollDistance;
/** Viewport position where a scroll-linked item starts revealing (its top edge). */
const SCROLL_START = 0.95;

/**
 * @param {number[] | "linear"} ease
 * @returns {string} a CSS easing-function value
 */
function cssEase(ease) {
  return typeof ease === "string" ? ease : `cubic-bezier(${ease.join(",")})`;
}

/**
 * Per-section appear config: the global Theme default (Settings › Animations)
 * merged with any per-section overrides emitted by `snippets/288-appear.liquid`
 * as data-attributes. An override attribute is present ONLY when the merchant
 * chose a non-"Theme default" value, so a missing/invalid attribute = inherit
 * the global. data-appear-ease overrides whichever mode actually runs.
 *
 *   data-appear-ease              movement curve (preset key)
 *   data-appear-duration          movement duration, ms       (in view)
 *   data-appear-opacity-duration  opacity duration, ms        (in view)
 *   data-appear-delay             stagger delay, ms           (in view)
 *   data-appear-step              stagger step, % viewport    (on scroll)
 *   data-appear-distance          movement distance, % vp     (on scroll)
 *
 * @param {HTMLElement} section
 */
function getSectionConfig(section) {
  const d = section.dataset;
  /** @param {string|undefined} v @param {number} fb */
  const ms = (v, fb) => {
    const n = parseFloat(String(v));
    return Number.isFinite(n) && n >= 0 ? n / 1000 : fb;
  };
  /** @param {string|undefined} v @param {number} fb */
  const frac = (v, fb) => {
    const n = parseFloat(String(v));
    return Number.isFinite(n) && n >= 0 ? n / 100 : fb;
  };
  /** @param {string|undefined} v @param {number[]|"linear"} fb */
  const ease = (v, fb) => (v && EASE_PRESETS[v] ? EASE_PRESETS[v] : fb);
  return {
    durationS: ms(d.appearDuration, DURATION_S),
    opacityDurationS: ms(d.appearOpacityDuration, OPACITY_DURATION_S),
    stepDelayS: ms(d.appearDelay, STEP_DELAY_S),
    stepScroll: frac(d.appearStep, STEP_SCROLL),
    scrollDistance: frac(d.appearDistance, SCROLL_DISTANCE),
    easeInView: ease(d.appearEase, EASE_IN_VIEW),
    easeScroll: ease(d.appearEase, EASE_SCROLL),
  };
}

/** @type {WeakMap<HTMLElement, () => void>} */
const staggerAppearCleanups = new WeakMap();

/**
 * Fires when any part of the section crosses the bottom-20%-of-viewport line.
 * amount:"some" lets tall mobile sections trigger without requiring 50%+ visibility.
 * Negative bottom margin shrinks the root inward so detection waits until
 * the section is actually ~20% from the bottom rather than the very edge.
 */
const INVIEW_AMOUNT = "some";
const INVIEW_MARGIN = "0px 0px -20% 0px";

/** @param {() => void} fn */
function whenReady(fn) {
  if (document.readyState !== "loading") {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Detect whether the browser is throttling requestAnimationFrame (e.g. iOS Low
 * Power Mode caps rAF — and CSS animations — to ~30fps). Continuous scroll-linked
 * scrubbing looks choppy at that cadence, so scroll mode falls back to a one-shot
 * staggered reveal. Measured once and cached globally across all sections.
 * @returns {Promise<{ fps: number, throttled: boolean }>}
 */
function detectFrameBudget() {
  if (window.__288FrameBudgetPromise) return window.__288FrameBudgetPromise;
  window.__288FrameBudgetPromise = new Promise((resolve) => {
    const SAMPLES = 12;
    const WARMUP = 3;
    /** @type {number[]} */
    const deltas = [];
    let last = 0;
    let count = 0;
    const tick = (/** @type {number} */ t) => {
      if (last) deltas.push(t - last);
      last = t;
      if (++count <= SAMPLES) {
        requestAnimationFrame(tick);
        return;
      }
      const usable = deltas.slice(WARMUP).sort((a, b) => a - b);
      const median = usable[Math.floor(usable.length / 2)] || 16;
      // > 26ms ≈ ≤ 38fps ⇒ treat as throttled.
      resolve({ fps: 1000 / median, throttled: median > 26 });
    };
    requestAnimationFrame(tick);
  });
  return window.__288FrameBudgetPromise;
}

/**
 * @param {HTMLElement} section
 */
function revealItemsStatic(section) {
  section.querySelectorAll(ITEM_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.opacity = "1";
      el.style.transform = "none";
    }
  });
}

/**
 * @param {HTMLElement} el
 * @param {object} transformKeyframes
 * @param {(target: Element, keyframes: object, options?: object) => unknown} animate
 * @param {number} delay
 * @param {ReturnType<typeof getSectionConfig>} cfg
 */
function animateAppearItem(el, transformKeyframes, animate, delay, cfg) {
  animate(
    el,
    { opacity: [0, 1] },
    {
      duration: cfg.opacityDurationS,
      ease: EASE_OPACITY,
      delay,
    },
  );
  animate(el, transformKeyframes, {
    duration: cfg.durationS,
    ease: cfg.easeInView,
    delay,
  });
}

/**
 * Returns item groups in reveal order.
 * - default: each item is its own group in DOM order
 * - pairs: items sharing the same data-appear-order are grouped together
 *
 * @param {HTMLElement[]} items
 * @param {boolean} usePairs
 * @returns {HTMLElement[][]}
 */
function buildAppearGroups(items, usePairs) {
  if (!usePairs) {
    return items.map((el) => [el]);
  }

  /** @type {Map<number, HTMLElement[]>} */
  const groups = new Map();
  items.forEach((el, domIndex) => {
    const raw = el.dataset.appearOrder;
    const parsed =
      raw !== undefined && raw !== "" ? parseInt(String(raw), 10) : NaN;
    const key = Number.isFinite(parsed) ? parsed : domIndex;
    if (!groups.has(key)) groups.set(key, []);
    const bucket = groups.get(key);
    if (bucket) bucket.push(el);
  });

  const sortedKeys = [...groups.keys()].sort((a, b) => a - b);
  return sortedKeys.map((key) => groups.get(key) ?? []);
}

/**
 * Reveal tier for a group at DOM index — how many stagger steps after the first.
 * - "linear" (default): sequential, tier = index.
 * - "radial": centre-out — tier = floor distance from the middle group, so the
 *   centre group(s) start at 0 and the edges reveal last (data-appear-sequence="radial").
 * @param {number} index
 * @param {number} count
 * @param {string} sequence
 */
function staggerTier(index, count, sequence) {
  if (sequence === "radial") {
    const center = (count - 1) / 2;
    return Math.floor(Math.abs(index - center));
  }
  return index;
}

/**
 * @param {HTMLElement} section
 * @returns {"linear"|"radial"}
 */
function getSequence(section) {
  return section.dataset.appearSequence === "radial" ? "radial" : "linear";
}

/**
 * @param {HTMLElement} section
 * @param {(target: Element, keyframes: object, options?: object) => unknown} animate
 */
function runStaggerAppear(section, animate) {
  if (prefersReducedMotion()) return;

  const items = [...section.querySelectorAll(ITEM_SELECTOR)].filter(
    (el) => el instanceof HTMLElement,
  );
  if (items.length === 0) {
    return;
  }

  const cfg = getSectionConfig(section);
  const style = section.dataset.appearStyle === "scale" ? "scale" : "translate";
  const transformKeyframes =
    style === "scale" ? { scale: [0.9, 1] } : { y: [40, 0] };

  const usePairs = section.dataset.appearPairs === "true";
  const sequence = getSequence(section);
  const groups = buildAppearGroups(items, usePairs);
  let maxTier = 0;
  groups.forEach((group, index) => {
    const tier = staggerTier(index, groups.length, sequence);
    if (tier > maxTier) maxTier = tier;
    group.forEach((el) => {
      animateAppearItem(el, transformKeyframes, animate, tier * cfg.stepDelayS, cfg);
    });
  });

  const settleMs =
    Math.ceil((maxTier * cfg.stepDelayS + cfg.durationS) * 1000) + 100;
  window.setTimeout(() => {
    revealItemsStatic(section);
  }, settleMs);
}

/**
 * Scroll-linked progressive mode with stagger.
 * Each item is its own scroll target. The animation plays as the item's top
 * edge travels from ~bottom of viewport to ~center. Stagger is created by
 * shifting each successive item's start/end points slightly higher, so later
 * items need a bit more scrolling before they begin.
 *
 * Once an item reaches progress >= 1 we commit its final state with inline
 * styles and cancel the scroll binding so it never reverses.
 *
 * @param {HTMLElement} section
 * @param {(target: Element, keyframes: object, options?: object) => unknown} animate
 * @param {(animation: unknown | ((progress: number, info: unknown) => void), options: object) => () => void} scroll
 * @returns {() => void} cleanup
 */
function initScrollMode(section, animate, scroll) {
  if (prefersReducedMotion()) {
    revealItemsStatic(section);
    return () => {};
  }

  const items = [...section.querySelectorAll(ITEM_SELECTOR)].filter(
    (el) => el instanceof HTMLElement,
  );
  if (items.length === 0) return () => {};

  const cfg = getSectionConfig(section);
  const sequence = getSequence(section);
  const style = section.dataset.appearStyle === "scale" ? "scale" : "translate";
  // Scroll mode drives one animation per item (opacity + transform together) on a
  // SINGLE scroll() binding. We deliberately do NOT split opacity into its own
  // linear scrub here: a second scroll() per item doubles the per-frame work and
  // janks pages with several scroll-linked sections. Opacity follows easeScroll;
  // the linear-opacity rule is kept for the In view modes where it's cheap.
  const transformKeyframes =
    style === "scale"
      ? { scale: [0.85, 1], opacity: [0, 1] }
      : { y: [80, 0], opacity: [0, 1] };

  const usePairs = section.dataset.appearPairs === "true";

  /** @type {Array<() => void>} */
  const cancels = [];

  const STAGGER_STEP = cfg.stepScroll;

  /**
   * @param {HTMLElement[]} els
   * @param {number} order
   */
  function revealGroup(els, order) {
    const shift = order * STAGGER_STEP;
    const startPoint = Math.min(SCROLL_START - shift, SCROLL_START);
    const endPoint = Math.max(SCROLL_START - cfg.scrollDistance - shift, 0.2);
    const endThresholdPx = endPoint * window.innerHeight;

    els.forEach((el) => {
      // Item already past its animation end point — reveal without binding.
      if (el.getBoundingClientRect().top <= endThresholdPx) {
        el.style.opacity = "1";
        el.style.transform = "none";
        return;
      }

      // Promote to a compositor layer so the scroll-linked transform/opacity is
      // GPU-composited rather than repainting each scroll frame (reduces jank).
      el.style.willChange = "transform, opacity";

      const offset = [`start ${startPoint}`, `start ${endPoint}`];
      const animation = animate(el, transformKeyframes, { ease: cfg.easeScroll });
      const cancel = scroll(animation, { target: el, offset });
      cancels.push(() => {
        cancel();
        el.style.willChange = "";
      });
    });
  }

  if (usePairs) {
    /** @type {Map<number, HTMLElement[]>} */
    const groups = new Map();
    items.forEach((el, domIndex) => {
      const raw = /** @type {HTMLElement} */ (el).dataset.appearOrder;
      const parsed =
        raw !== undefined && raw !== "" ? parseInt(String(raw), 10) : NaN;
      const key = Number.isFinite(parsed) ? parsed : domIndex;
      if (!groups.has(key)) groups.set(key, []);
      const bucket = groups.get(key);
      if (bucket) bucket.push(/** @type {HTMLElement} */ (el));
    });
    const sortedKeys = [...groups.keys()].sort((a, b) => a - b);
    sortedKeys.forEach((key, pairIdx) => {
      const els = groups.get(key) ?? [];
      revealGroup(els, staggerTier(pairIdx, sortedKeys.length, sequence));
    });
  } else {
    items.forEach((el, index) => {
      revealGroup([el], staggerTier(index, items.length, sequence));
    });
  }

  return () => {
    cancels.forEach((c) => c());
  };
}

/**
 * Scroll-linked scrubbing janks on mobile: Safari's address-bar toggle fires a
 * burst of `resize` events while scrolling and every per-item scroll() binding
 * recomputes its offsets mid-scroll. Below this width, scroll mode falls back to
 * the one-shot intersection reveal (no per-frame scrubbing, no resize recompute).
 * Desktop keeps the scroll-linked mode (no address bar there).
 */
const SCROLL_MODE_MIN_WIDTH = 750;

/**
 * Effective appear mode for the current viewport.
 * @param {HTMLElement} section
 * @returns {"intersection"|"scroll"}
 */
function getMode(section) {
  const raw = section.dataset.appearMode === "scroll" ? "scroll" : "intersection";
  if (raw === "scroll" && window.innerWidth < SCROLL_MODE_MIN_WIDTH) {
    return "intersection";
  }
  return raw;
}

/**
 * @param {HTMLElement} section
 */
function initSection(section) {
  if (section.dataset.staggerAppearBound === "true") return;

  section.dataset.staggerAppearBound = "true";

  /** @type {() => void} */
  let cleanup = () => {};

  staggerAppearCleanups.set(section, () => {
    cleanup();
    delete section.dataset.staggerAppearBound;
  });

  if (prefersReducedMotion()) return;

  resetSectionInitialState(section);

  const mode = getMode(section);

  // Sections already visible in the viewport at init time don't need intersection
  // detection or a CDN fetch — treat them as eager automatically.
  const inViewAtInit =
    mode === "intersection" &&
    section.getBoundingClientRect().top < window.innerHeight;
  const effectiveMode = inViewAtInit ? "eager" : mode;

  // Eager mode: animate directly using native Web Animation API — no CDN.
  if (effectiveMode === "eager") {
    const cfg = getSectionConfig(section);
    const style = section.dataset.appearStyle === "scale" ? "scale" : "translate";
    const usePairs = section.dataset.appearPairs === "true";
    const items = [...section.querySelectorAll(ITEM_SELECTOR)].filter(
      (el) => el instanceof HTMLElement,
    );

    const sequence = getSequence(section);
    const groups = buildAppearGroups(items, usePairs);
    groups.forEach((group, index) => {
      const delay = staggerTier(index, groups.length, sequence) * cfg.stepDelayS * 1000;
      group.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: cfg.opacityDurationS * 1000, delay, easing: EASE_OPACITY, fill: "both" },
        );
        el.animate(
          style === "scale"
            ? [{ transform: "scale(0.9)" }, { transform: "none" }]
            : [{ transform: "translateY(40px)" }, { transform: "none" }],
          { duration: cfg.durationS * 1000, delay, easing: cssEase(cfg.easeInView), fill: "both" },
        ).onfinish = () => {
          el.style.opacity = "1";
          el.style.transform = "none";
        };
      });
    });
    return;
  }

  import("@theme/motion")
    .then(async (motion) => {
      if (section.dataset.staggerAppearBound !== "true") return;
      const { inView, animate, scroll } = motion;

      // Scroll mode scrubs per-item on every frame; when rAF is throttled
      // (iOS Low Power Mode) that looks choppy, so fall back to the one-shot
      // staggered reveal which reads cleanly even at ~30fps.
      const { throttled } = await detectFrameBudget();
      if (section.dataset.staggerAppearBound !== "true") return;

      if (mode === "scroll" && !throttled) {
        cleanup = initScrollMode(section, animate, scroll);
      } else {
        cleanup = inView(
          section,
          () => {
            void runStaggerAppear(section, animate);
          },
          {
            amount: INVIEW_AMOUNT,
            margin: INVIEW_MARGIN,
          },
        );
      }
    })
    .catch(() => {
      if (section.dataset.staggerAppearBound !== "true") return;
      revealItemsStatic(section);
    });
}

/**
 * @param {HTMLElement} section
 */
function destroySection(section) {
  const fn = staggerAppearCleanups.get(section);
  if (typeof fn === "function") fn();
  staggerAppearCleanups.delete(section);
}

function run() {
  document.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initSection(el);
  });
}

/**
 * @param {HTMLElement} section
 */
function resetSectionInitialState(section) {
  const style = section.dataset.appearStyle === "scale" ? "scale" : "translate";
  const isScrollMode = getMode(section) === "scroll";
  const viewportHeight = window.innerHeight;

  section.querySelectorAll(ITEM_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    // Scroll-linked animations can jump when their target is already in view.
    // Intersection animations should stay hidden here so they can reveal cleanly.
    if (isScrollMode && el.getBoundingClientRect().top < viewportHeight) {
      el.style.opacity = "1";
      el.style.transform = "none";
      return;
    }
    el.style.opacity = "0";
    if (style === "scale") {
      el.style.transform = isScrollMode ? "scale(0.85)" : "scale(0.9)";
    } else {
      el.style.transform = isScrollMode
        ? "translateY(80px)"
        : "translateY(40px)";
    }
  });
}

function replayAllSections() {
  document.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    destroySection(el);
    resetSectionInitialState(el);
    initSection(el);
  });
}

function scheduleRun() {
  /** @type {Window & { themeAfterPageReveal?: (fn: () => void) => void }} */
  const themeWindow = window;

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    run();
  };

  document.addEventListener("theme:load-animations-start", start, { once: true });

  if (typeof themeWindow.themeAfterPageReveal === "function") {
    themeWindow.themeAfterPageReveal(() => {
      window.setTimeout(start, 200);
    });
  } else {
    whenReady(() => {
      requestAnimationFrame(() => requestAnimationFrame(start));
    });
  }
}

scheduleRun();

window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    replayAllSections();
  }
});

document.addEventListener("shopify:section:load", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const sections = t.matches(SECTION_SELECTOR)
    ? [t]
    : [...t.querySelectorAll(SECTION_SELECTOR)];
  sections.forEach((el) => {
    if (el instanceof HTMLElement) initSection(el);
  });
});

document.addEventListener("shopify:section:unload", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const sections = t.matches(SECTION_SELECTOR)
    ? [t]
    : [...t.querySelectorAll(SECTION_SELECTOR)];
  sections.forEach((el) => {
    if (el instanceof HTMLElement) destroySection(el);
  });
});

export {};
